import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import OutletRating from "./OutletRating";

const AUTO_ACTIONS = [
  { value: "approve", label: "Approve", description: "Auto-approve requests (visible via API/search, not in feed)" },
  {
    value: "approve_and_distribute",
    label: "Approve + Feed",
    description: "Auto-approve and show in the promoter feed",
  },
  {
    value: "approve_and_notify",
    label: "Approve + Feed & Notify",
    description: "Auto-approve, show in feed, and send notifications",
  },
  { value: "ghost", label: "Ghost", description: "Auto-ghost requests from this outlet" },
  { value: "manual_review", label: "Manual Review", description: "Route requests to manual review queue" },
];

function OutletConfigModal({ outletId, outletName, currentConfig, outletInfo, onSave, onClose }) {
  const [autoAction, setAutoAction] = useState(currentConfig?.auto_action || "manual_review");
  const [setFree, setSetFree] = useState(currentConfig?.set_free || false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const headers = {
        ...authenticatedHeadersHash(),
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      };

      // Save config
      let configResponse;
      if (currentConfig?.id) {
        configResponse = await fetch(`/admin/api/request_approval_configs/${currentConfig.id}`, {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            request_approval_config: { auto_action: autoAction, set_free: setFree },
          }),
        });
      } else {
        configResponse = await fetch("/admin/api/request_approval_configs", {
          method: "POST",
          headers,
          body: JSON.stringify({
            request_approval_config: {
              configurable_type: "Id::MediaOutlet",
              configurable_id: outletId,
              auto_action: autoAction,
              set_free: setFree,
            },
          }),
        });
      }

      if (!configResponse.ok) {
        const data = await configResponse.json();
        setError(data.errors?.join(", ") || "Failed to save config");
        return;
      }

      const updatedConfig = await configResponse.json();

      // Post reason as comment if provided
      if (reason.trim()) {
        await fetch("/admin/api/comments", {
          method: "POST",
          headers,
          body: JSON.stringify({
            comment: {
              commentable_type: "Id::MediaOutlet",
              commentable_id: outletId,
              body: reason.trim(),
            },
          }),
        });
      }

      onSave(updatedConfig);
    } catch (err) {
      setError("Network error - please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Set Auto Action for {outletName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          {/* Left column: config options */}
          <div className="col-md-7">
            <h6 className="text-muted mb-3">Auto Action</h6>
            {AUTO_ACTIONS.map((action) => (
              <div className="form-check mb-2" key={action.value}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="autoAction"
                  id={`action-${action.value}`}
                  value={action.value}
                  checked={autoAction === action.value}
                  onChange={() => setAutoAction(action.value)}
                />
                <label className="form-check-label" htmlFor={`action-${action.value}`}>
                  <strong>{action.label}</strong>
                  <br />
                  <small className="text-muted">{action.description}</small>
                </label>
              </div>
            ))}

            <hr />

            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="setFree"
                checked={setFree}
                onChange={(event) => setSetFree(event.target.checked)}
              />
              <label className="form-check-label" htmlFor="setFree">
                Set as free to pitch
              </label>
            </div>

            <div className="mb-3">
              <label htmlFor="reason" className="form-label text-muted small">
                Reason (optional)
              </label>
              <textarea
                id="reason"
                className="form-control form-control-sm"
                rows="2"
                placeholder="Why are you setting this rule?"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
          </div>

          {/* Right column: outlet info */}
          <div className="col-md-5">
            <div className="bg-light p-3 rounded">
              <h6>{outletName}</h6>
              <OutletRating
                prestige={outletInfo?.prestige}
                domainAuthority={outletInfo?.domainAuthority}
                variant="large"
              />
              {outletInfo?.requestCount != null && (
                <div className="d-flex justify-content-between small mb-1">
                  <span className="text-muted">Total Requests</span>
                  <strong>{outletInfo.requestCount}</strong>
                </div>
              )}
              {outletInfo?.mediaPersonCount != null && (
                <div className="d-flex justify-content-between small mb-1">
                  <span className="text-muted">Reporters</span>
                  <strong>{outletInfo.mediaPersonCount}</strong>
                </div>
              )}
              {currentConfig && (
                <>
                  <hr />
                  <div className="small">
                    <span className="text-muted">Current: </span>
                    <span className="badge bg-secondary">{currentConfig.auto_action}</span>
                    {currentConfig.set_free && <span className="badge bg-outline-info ms-1">Free</span>}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default OutletConfigModal;
